<script setup lang="ts">
import type { DynamicAuctionItemDetails } from '@autobid/strapi-integration/rest/components'
import type { PreviewItemData } from '@autobid/strapi-integration/typescript/auction/carPreview'
import type { CarImageSize, DetailsCarItemData } from '@autobid/ui/types/Car'
import type { DetailsCarItemDetails } from '@autobid/ui/types/components/CarDetails'
import { useCarGallery } from '@autobid/ui/composables/car/useCarGallery'
import { usePrepareDetailsCarData } from '@autobid/ui/composables/car/usePrepareDetailsCarData'
import { useIsMobile } from '@autobid/ui/composables/useIsMobile'
import { generateImageSizesFromArray } from '@autobid/ui/utils/car/generateImageSizes'
import { computed } from 'vue'

type Props = Partial<DynamicAuctionItemDetails> & {
  car: DetailsCarItemData | PreviewItemData
  details: DetailsCarItemDetails
  preview?: boolean
}

const props = defineProps<Props>()
const isMobile = useIsMobile()
const { demurageeFee, generalInformation, goft } = usePrepareDetailsCarData(
  computed(() => props.details),
)
const mainImage = computed(() => {
  return props.details.gallery[0]
    ? generateImageSizesFromArray('hd', [props.details.gallery[0]])[0]
    : null
})

const showGallery = ref(false)
const { provideShowGallery, openCarGallery } = useCarGallery()
provideShowGallery(showGallery)

function handleClickImage(links: Record<CarImageSize, string>) {
  showGallery.value = true
  openCarGallery(links)
}
</script>

<template>
  <div class="car-details w-full">
    <SectionsDetailsGallery :images="details.gallery" />

    <div class="mt-3.5 flex items-center justify-between">
      <div class="w-full pr-0 md:pr-4 lg:pr-8">
        <div class="w-full bg-[#F8F8F8] p-4">
          <header
            class="font-bold leading-7 text-primary md:text-xl"
          >
            {{ details.headlineDescription }}
          </header>
          <p class="text-sm text-onAccent1">
            {{ details.generalDescriptionHeader }}
          </p>
        </div>
      </div>
      <div class="w-max">
        <!-- icons desktop -->
        <div class="hidden w-full items-center gap-2 md:flex">
          <ElementsCarAdditionInfo :car="car" class="ml-auto" />

          <ElementsCarStaticActions
            v-if="!preview"
            :sca-popup-slug="scaPopupSlug"
            :car="car"
            size="big"
          />
        </div>
      </div>
    </div>

    <div class="mt-3 flex flex-wrap md:flex-nowrap">
      <div class="w-full md:mr-2.5 md:w-1/3 lg:w-96 lg:min-w-[384px]">
        <div class="relative w-full">
          <div v-if="car.isHighestBidder" class="absolute left-2 top-2 z-10">
            <ElementsCarHighestBidderTooltip />
          </div>

          <button
            v-if="mainImage"
            class="mb-2 flex w-full items-center justify-center bg-black sm:w-2/3 md:mb-0 md:w-full"
            :title="$t('car-details.open-gallery')"
            @click="handleClickImage(mainImage.links)"
          >
            <img
              class="flex h-auto max-h-[480px] w-full max-w-full object-contain sm:max-h-[576px] md:max-h-[384px] lg:max-h-[292px] [@media(max-width:440px)]:max-h-[324px]"
              :src="mainImage.links.l"
              :alt="mainImage.description?.categoryLabel ?? ''"
            >
          </button>
          <ElementsCarThumbnailPlaceholder v-else />
        </div>

        <!-- icons mobile -->
        <div
          class="details-icon-wrapper mb-2 flex w-full flex-wrap items-center justify-between gap-2.5 md:mt-3.5 md:items-start md:justify-start lg:mb-6"
        >
          <div class="flex items-center gap-2.5 md:!hidden">
            <ElementsCarStaticActions
              v-if="!preview"
              :sca-popup-slug="scaPopupSlug"
              :car="car"
              size="small"
            />

            <ElementsCarAdditionInfo :car="car" />
          </div>

          <div
            class="ml-auto flex flex-wrap items-end gap-2.5 md:ml-0 md:w-full md:items-center lg:flex-nowrap"
          >
            <ElementsCarSupplierLogo
              :car="car"
              class="details-supplier-logo flex w-32 justify-end sm:w-48 md:order-2 md:ml-auto md:mt-2 md:w-32 lg:mt-0 lg:w-full"
            />

            <div class="flex gap-2.5">
              <ElementsCarDynamicActions
                v-if="!preview"
                :car="car"
                size="big"
                class="details-dynamic-icon"
              />
            </div>
          </div>
        </div>

        <LazySectionsDetailsSlider
          v-if="!isMobile"
          :is-highest-bidder="car.isHighestBidder"
          :gallery="details.gallery"
        />
      </div>

      <div class="mt-4 w-full md:mt-0 md:w-2/3 lg:w-full">
        <SectionsDetailsSectionsInformation
          :car="car"
          :information="details.information"
          :pdf-slug="pdfSlug"
        />
      </div>
    </div>

    <div class="mt-5 grid grid-cols-12 gap-2.5">
      <LazySectionsDetailsSectionsDocuments
        :car="car"
        :details="details"
        :tax-popup-slug="taxPopupSlug"
      />
      <LazySectionsDetailsSectionsAccidentPredamages :details="details" />
      <LazySectionsDetailsSectionsEngineTransmissionTurbo :details="details" />
      <LazySectionsDetailsSectionsExtras :details="details" />
      <LazySectionsDetailsElementsTitleWithContent :data="goft" />
      <LazySectionsDetailsElementsTitleWithContent :data="demurageeFee" />
      <LazySectionsDetailsSectionsSpecialExtras :details="details" />
      <LazySectionsDetailsSectionsSeats :details="details" />
      <LazySectionsDetailsElementsTitleWithContent :data="generalInformation" />
      <LazySectionsDetailsSectionsAccidentInfo :details="details" />
      <LazySectionsDetailsSectionsImagePanelInstrument :details="details" />
      <LazySectionsDetailsSectionsImageAdditionalDetails :details="details" />
      <LazySectionsDetailsSectionsImageBeautyDetails :details="details" />
      <LazySectionsDetailsSectionsWheels
        :wheels="details.wheels"
        :title="$t('car-details.section.wheels')"
      />
      <LazySectionsDetailsSectionsWheels
        :wheels="details.secondWheels"
        :title="$t('car-details.section.second-wheels')"
      />
      <LazySectionsDetailsSectionsPart
        :part="details.imagesExterior"
        part-name="exterior"
      />
      <LazySectionsDetailsSectionsPart
        :part="details.imagesInterior"
        part-name="interior"
      />
      <LazySectionsDetailsSectionsPart
        :part="details.imagesWindshield"
        part-name="windshield"
      />
      <LazySectionsDetailsSectionsFoundDefects :details="details" />
      <LazySectionsDetailsSectionsMissingParts :details="details" />
      <LazySectionsDetailsSectionsManufacturerInformation :details="details" />
    </div>
  </div>
</template>

<style lang="scss">
.car-details {
  .td-image {
    width: 74px;
    min-width: 74px;
    max-width: 74px;
  }
}
</style>
